import type { ReactNode } from "react";
import { LuX } from "react-icons/lu";
import {
  Box,
  CloseButton,
  Drawer,
  Heading,
  HStack,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";

type DrawerSidebarProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
};

export function DrawerSidebar({
  open,
  onClose,
  title,
  subtitle,
  description,
  icon,
  children,
  footer,
  maxWidth = "xl",
}: DrawerSidebarProps) {
  return (
    <Drawer.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} size="xl" placement="end">
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content maxW={maxWidth}>
            <Drawer.Header borderBottomWidth="1px" borderColor="border" px={6} py={5}>
              <Stack gap={1} flex="1">
                {icon || subtitle ? (
                  <HStack gap={3} color="blue.500">
                    {icon}
                    {subtitle ? (
                      <Text fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em">
                        {subtitle}
                      </Text>
                    ) : null}
                  </HStack>
                ) : null}
                <Heading size="xl">{title}</Heading>
                {description ? (
                  <Text mt={1} fontSize="sm" color="fg.muted">{description}</Text>
                ) : null}
              </Stack>
              <Drawer.CloseTrigger asChild>
                <CloseButton size="sm" aria-label="Close sidebar">
                  <LuX />
                </CloseButton>
              </Drawer.CloseTrigger>
            </Drawer.Header>
            <Drawer.Body p={0} display="flex" flexDirection="column" minH={0}>
              <Box flex="1" display="flex" flexDirection="column" overflowY="auto">
                {children}
              </Box>
            </Drawer.Body>
            {footer ? (
              <Drawer.Footer borderTopWidth="1px" borderColor="border" px={6} py={5}>
                {footer}
              </Drawer.Footer>
            ) : null}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
